import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  CircularProgress,
  TextField,
  InputAdornment,
} from '@mui/material';
import { useOutletContext } from 'react-router-dom';
import { MdSearch, MdErrorOutline } from 'react-icons/md';
import { jwtDecode } from 'jwt-decode';
import theme from './theme';
import api from '../utils/api';
import DashboardInsightCard from './DashboardInsightCard';

/** Prefer JWT (login token); fall back to persisted email from login flow. */
function getEmailFromLoginToken() {
  const token = localStorage.getItem('token');
  if (token) {
    try {
      const decoded = jwtDecode(token);
      if (decoded?.email) return String(decoded.email).trim();
    } catch (e) {
      console.error('RoomsPage: token decode error', e);
    }
  }
  return (localStorage.getItem('email') ?? '').trim();
}

/** Mockup-aligned tokens */
const CANVAS = '#F9F9F7';
const ALERT_BG = '#FEF3E2';

function roomSessionCount(room) {
  const n = Number(room?.totalSessions ?? room?.sessions ?? 0);
  return Number.isFinite(n) ? n : 0;
}

function roomHasSessions(room) {
  return roomSessionCount(room) > 0;
}

function roomRecencySortKey(room) {
  const d = room?.daysSinceLastDisinfection;
  if (d == null || d === '') return Number.POSITIVE_INFINITY;
  const n = Number(d);
  return Number.isFinite(n) ? n : Number.POSITIVE_INFINITY;
}

function roomTypeCategory(type) {
  const t = String(type ?? '')
    .trim()
    .toUpperCase();
  if (t.includes('ICU')) return 'ICU';
  if (t.includes('OT') || t.includes('THEAT')) return 'OT';
  if (t.includes('OP') || t.includes('OUTPAT')) return 'OP';
  if (t.includes('PATIENT') || t.includes('WARD') || t.includes('GEN')) return 'Patient';
  return 'Other';
}

function typeHeadingLabel(type) {
  const cat = roomTypeCategory(type);
  if (cat === 'OP') return 'OP (Outpatient)';
  if (cat === 'OT') return 'OT (Operating theatre)';
  if (cat === 'ICU') return 'ICU';
  if (cat === 'Patient') return 'Patient area';
  return String(type || 'Room').trim() || 'Room';
}

function formatShortDaysAgo(days) {
  if (days == null || days === '') return '—';
  const n = Number(days);
  if (!Number.isFinite(n)) return '—';
  if (n <= 0) return 'Today';
  return `${Math.round(n)}d ago`;
}

function formatLifetimeHours(h) {
  if (h == null || h === '') return '—';
  const n = Number(h);
  if (!Number.isFinite(n)) return '—';
  return `${n.toFixed(1)} h`;
}

function formatAvgMinutes(mins) {
  if (mins == null || mins === '') return '—';
  const n = Number(mins);
  if (!Number.isFinite(n)) return '—';
  return `${Math.round(n)} min`;
}

function firstFiniteNumber(...vals) {
  for (const v of vals) {
    if (v == null || v === '') continue;
    const n = Number(v);
    if (Number.isFinite(n)) return n;
  }
  return null;
}

/** Minutes for one session row (API shapes + human-readable duration strings). */
function sessionDurationMinutes(s) {
  if (!s || typeof s !== 'object') return null;
  const direct = firstFiniteNumber(
    s.durationMinutes,
    s.duration_minutes,
    s.durationMins,
    s.minutes,
    s.durationMin,
    s.avgDurationMinutes,
  );
  if (direct != null) return direct;
  const sec = firstFiniteNumber(s.durationSeconds, s.duration_seconds);
  if (sec != null) return sec / 60;
  const ms = firstFiniteNumber(s.durationMs, s.duration_ms);
  if (ms != null) return ms / 60000;
  const hours = firstFiniteNumber(s.durationHours, s.duration_hours);
  if (hours != null) return hours * 60;
  const raw = s.duration ?? s.durationLabel ?? '';
  if (typeof raw === 'number' && Number.isFinite(raw)) return raw;
  if (typeof raw !== 'string') return null;
  const str = raw.trim().toLowerCase();
  const minMatch = str.match(/([\d.]+)\s*(min|mins|minutes)\b/);
  if (minMatch) return Number(minMatch[1]);
  const hMatch = str.match(/([\d.]+)\s*(h|hr|hrs|hours)\b/);
  if (hMatch) return Number(hMatch[1]) * 60;
  return null;
}

function sumSessionMinutes(sessions) {
  if (!Array.isArray(sessions)) return 0;
  let t = 0;
  for (const s of sessions) {
    const m = sessionDurationMinutes(s);
    if (m != null && Number.isFinite(m) && m > 0) t += m;
  }
  return t;
}

function averageSessionMinutes(sessions) {
  if (!Array.isArray(sessions) || sessions.length === 0) return null;
  const mins = sessions.map(sessionDurationMinutes).filter((m) => m != null && Number.isFinite(m) && m > 0);
  if (mins.length === 0) return null;
  return mins.reduce((a, b) => a + b, 0) / mins.length;
}

function pickLifetimeHoursFromApi(d, room) {
  return firstFiniteNumber(
    d.lifetimeHours,
    d.lifetime_hours,
    d.lifetimeUvcHours,
    d.lifetime_uvc_hours,
    d.totalUvcHours,
    d.total_uvc_hours,
    d.uvcHours,
    d.uvc_hours,
    d.totalHours,
    d.total_hours,
    room?.lifetimeHours,
    room?.lifetime_hours,
    room?.lifetimeUvcHours,
    room?.totalUvcHours,
  );
}

function pickAvgMinutesFromApi(d, room) {
  return firstFiniteNumber(
    d.avgDurationMinutes,
    d.avg_duration_min,
    d.avg_session_minutes,
    d.avgSessionMinutes,
    d.avgDuration,
    d.averageDurationMinutes,
    d.meanDurationMinutes,
    room?.avgDurationMinutes,
    room?.avg_duration_min,
    room?.avgSessionMinutes,
  );
}

function formatSessionLine(s) {
  if (s?.label) return s.label;
  const date = s?.date ?? s?.startDate ?? '';
  const timeRange = s?.timeRange ?? s?.range ?? '';
  if (date && timeRange) return `${date} · ${timeRange}`;
  return date || timeRange || '—';
}

function formatSessionRight(s) {
  const dur = s?.duration ?? s?.durationLabel ?? '';
  const mode = s?.method ?? s?.mode ?? '';
  if (dur && mode) return `${dur} · ${mode}`;
  return dur || mode || '—';
}

function RoomListRow({ room, selected, onSelect }) {
  const daysSince = room.daysSinceLastDisinfection ?? null;
  const isOverdue = daysSince !== null && Number(daysSince) > 14;
  const sessions = room.totalSessions ?? 0;
  const life = formatLifetimeHours(pickLifetimeHoursFromApi(room, {}));

  return (
    <Paper
      elevation={0}
      onClick={() => onSelect(room)}
      sx={{
        px: 2,
        py: 1.5,
        borderRadius: '10px',
        cursor: 'pointer',
        border: `1px solid ${isOverdue ? 'rgba(251, 191, 36, 0.5)' : theme.colors.borderDefault}`,
        bgcolor: selected ? 'rgba(241, 245, 249, 0.95)' : isOverdue ? 'rgba(254, 243, 226, 0.65)' : theme.colors.bgPrimary,
        transition: 'background 0.15s, border-color 0.15s',
        '&:hover': {
          borderColor: theme.colors.brandPrimary,
          boxShadow: '0 2px 8px rgba(15, 23, 42, 0.06)',
        },
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 0.5 }}>
        <Typography sx={{ fontSize: 15, fontWeight: 700, color: theme.colors.textPrimary }}>{room.name}</Typography>
        <Typography
          sx={{
            fontSize: 13,
            fontWeight: 600,
            color: isOverdue ? theme.colors.accentWarning : theme.colors.textSecondary,
          }}
        >
          {formatShortDaysAgo(daysSince)}
        </Typography>
      </Box>
      <Typography sx={{ fontSize: 12, color: theme.colors.textSecondary }}>
        {sessions} sessions · {life} lifetime
      </Typography>
    </Paper>
  );
}

export default function RoomsPage() {
  const { clientEmail } = useOutletContext();
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [roomDetail, setRoomDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [search, setSearch] = useState('');
  /** 'all' | 'with' | 'without' — filter list by whether the room has any disinfection sessions */
  const [sessionFilter, setSessionFilter] = useState('with');

  useEffect(() => {
    const fetchRooms = async () => {
      try {
        const email = clientEmail || getEmailFromLoginToken();
        if (!email) {
          setRooms([]);
          return;
        }

        const robotsRes = await api.get(`/getrobots/${encodeURIComponent(email)}`);
        const robots = Array.isArray(robotsRes.data) ? robotsRes.data : [];
        const robotIds = robots
          .map((r) => r.robotId ?? r.robot_id ?? r.id)
          .filter(Boolean)
          .join(',');

        const res = await api.get('/client/rooms', {
          params: { email, robotIds },
        });
        if (res.data?.success) {
          setRooms(res.data.rooms || []);
        } else {
          setRooms([]);
        }
      } catch (e) {
        console.error('Rooms fetch error:', e);
        setRooms([]);
      } finally {
        setLoading(false);
      }
    };
    fetchRooms();
  }, [clientEmail]);

  const handleRoomSelect = useCallback(async (room) => {
    setSelectedRoom(room);
    setRoomDetail(null);
    setDetailLoading(true);
    try {
      const email = clientEmail || getEmailFromLoginToken();
      if (!email) return;
      const res = await api.get(`/client/rooms/${room.id}/detail`, { params: { email } });
      if (res.data?.success) setRoomDetail(res.data);
    } catch (e) {
      console.error('Room detail error:', e);
    } finally {
      setDetailLoading(false);
    }
  }, [clientEmail]);

  const sessionChipCounts = useMemo(() => {
    const q = search.trim().toLowerCase();
    const matchesSearch = (r) => !q || String(r.name ?? '')
      .toLowerCase()
      .includes(q);
    const base = rooms.filter(matchesSearch);
    const counts = { withSession: 0, withoutSession: 0 };
    for (const r of base) {
      if (roomHasSessions(r)) counts.withSession += 1;
      else counts.withoutSession += 1;
    }
    return counts;
  }, [rooms, search]);

  const visibleRooms = useMemo(() => {
    const q = search.trim().toLowerCase();
    let list = rooms.filter((r) => !q || String(r.name ?? '')
      .toLowerCase()
      .includes(q));
    if (sessionFilter === 'with') {
      list = list.filter((r) => roomHasSessions(r));
    } else if (sessionFilter === 'without') {
      list = list.filter((r) => !roomHasSessions(r));
    }
    return [...list].sort((a, b) => {
      const recencyDiff = roomRecencySortKey(a) - roomRecencySortKey(b);
      if (recencyDiff !== 0) return recencyDiff;
      return String(a.name || '').localeCompare(String(b.name || ''));
    });
  }, [rooms, search, sessionFilter]);

  const attentionCount = useMemo(
    () => rooms.filter((r) => r.daysSinceLastDisinfection != null && Number(r.daysSinceLastDisinfection) > 14).length,
    [rooms],
  );

  useEffect(() => {
    if (loading || selectedRoom) return;
    if (visibleRooms.length > 0) {
      handleRoomSelect(visibleRooms[0]);
    }
  }, [loading, visibleRooms, selectedRoom, handleRoomSelect]);

  useEffect(() => {
    if (visibleRooms.length === 0) {
      setSelectedRoom(null);
      setRoomDetail(null);
      return;
    }
    if (!selectedRoom) return;
    const stillThere = visibleRooms.some((r) => (r.id ?? r.name) === (selectedRoom.id ?? selectedRoom.name));
    if (!stillThere) {
      handleRoomSelect(visibleRooms[0]);
    }
  }, [visibleRooms, selectedRoom, handleRoomSelect]);

  const stats = useMemo(() => {
    if (!selectedRoom) return null;
    const d = roomDetail || {};
    const recent = d.recentSessions ?? [];
    const sess = firstFiniteNumber(d.totalSessions, d.sessions, selectedRoom.totalSessions, selectedRoom.sessions);

    const avgFromApi = pickAvgMinutesFromApi(d, selectedRoom);
    const avgFromSessions = averageSessionMinutes(recent);
    const avgMin = firstFiniteNumber(avgFromApi, avgFromSessions);

    let lifeHours = pickLifetimeHoursFromApi(d, selectedRoom);
    if (lifeHours == null) {
      const sumMin = sumSessionMinutes(recent);
      if (sumMin > 0) lifeHours = sumMin / 60;
    }
    if (lifeHours == null && avgMin != null && sess != null && sess > 0) {
      lifeHours = (avgMin * sess) / 60;
    }

    const last = selectedRoom.daysSinceLastDisinfection;
    return {
      lifetime: formatLifetimeHours(lifeHours),
      sessions: sess != null && sess !== '' ? String(sess) : '—',
      avgDuration: formatAvgMinutes(avgMin),
      lastSession: formatShortDaysAgo(last),
    };
  }, [selectedRoom, roomDetail]);

  const roomInsightSource = useMemo(() => {
    const v = roomDetail?.insightsFromView;
    const hasPoints = Array.isArray(v?.points) && v.points.length > 0;
    const hasNarrative = typeof v?.narrative === 'string' && v.narrative.trim().length > 0;
    return hasPoints || hasNarrative ? 'ai' : 'template';
  }, [roomDetail]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress sx={{ color: theme.colors.brandPrimary }} />
      </Box>
    );
  }

  return (
    <Box sx={{ bgcolor: CANVAS, mx: { xs: -2, md: -3 }, mt: -3, mb: -3, px: { xs: 2, md: 3 }, py: 3 }}>
      {/* Attention banner */}
      {attentionCount > 0 && (
        <Paper
          elevation={0}
          sx={{
            mb: 2.5,
            p: 2,
            borderRadius: `${theme.radius.md}px`,
            bgcolor: ALERT_BG,
            border: '1px solid rgba(251, 191, 36, 0.35)',
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            flexWrap: 'wrap',
          }}
        >
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '50%',
              bgcolor: 'rgba(217, 119, 6, 0.15)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <MdErrorOutline size={22} color={theme.colors.accentWarning} />
          </Box>
          <Box sx={{ flex: 1, minWidth: 200 }}>
            <Typography sx={{ fontSize: 15, fontWeight: 700, color: theme.colors.textPrimary, mb: 0.25 }}>
              {attentionCount} room{attentionCount === 1 ? '' : 's'} need attention
            </Typography>
            <Typography sx={{ fontSize: 13, color: theme.colors.textSecondary }}>
              Not disinfected in over 14 days — prioritise tonight
            </Typography>
          </Box>
        </Paper>
      )}

      <Grid container spacing={2.5} alignItems="flex-start">
        {/* Left: room list (~30%) */}
        <Grid item xs={12} md={4}>
          <Paper
            elevation={0}
            sx={{
              p: 2,
              borderRadius: `${theme.radius.lg}px`,
              border: `1px solid ${theme.colors.borderDefault}`,
              bgcolor: theme.colors.bgPrimary,
            }}
          >
            <TextField
              fullWidth
              size="small"
              placeholder="Search rooms..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <MdSearch size={20} color={theme.colors.textSecondary} />
                  </InputAdornment>
                ),
              }}
              sx={{
                mb: 2,
                '& .MuiOutlinedInput-root': {
                  borderRadius: '10px',
                  bgcolor: theme.colors.bgSecondary,
                  fontSize: 14,
                },
              }}
            />

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 2 }}>
              {[
                { mode: 'with', label: 'With session', count: sessionChipCounts.withSession },
                { mode: 'without', label: 'Without session', count: sessionChipCounts.withoutSession },
              ].map(({ mode, label, count }) => {
                const active = sessionFilter === mode;
                return (
                  <Box
                    key={mode}
                    component="button"
                    type="button"
                    onClick={() => setSessionFilter((prev) => (prev === mode ? 'all' : mode))}
                    sx={{
                      border: 'none',
                      cursor: 'pointer',
                      borderRadius: 999,
                      px: 1.5,
                      py: 0.5,
                      fontSize: 13,
                      fontWeight: 600,
                      fontFamily: 'inherit',
                      bgcolor: active ? 'rgba(241, 245, 249, 0.95)' : 'transparent',
                      color: active ? theme.colors.textPrimary : theme.colors.textSecondary,
                      boxShadow: active ? 'inset 0 0 0 1px rgba(226, 232, 240, 0.9)' : 'none',
                      '&:hover': { bgcolor: 'rgba(241, 245, 249, 0.8)' },
                    }}
                  >
                    {`${label} ${count}`}
                  </Box>
                );
              })}
            </Box>

            {visibleRooms.length === 0 ? (
              <Typography sx={{ fontSize: 14, color: theme.colors.textSecondary, py: 2, textAlign: 'center' }}>
                No rooms match your filters.
              </Typography>
            ) : (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1,
                  minHeight: 0,
                  maxHeight: { xs: '52vh', sm: '56vh', md: 'calc(100dvh - 260px)' },
                  overflowY: 'auto',
                  pr: 0.5,
                  WebkitOverflowScrolling: 'touch',
                  '&::-webkit-scrollbar': { width: 8 },
                  '&::-webkit-scrollbar-thumb': {
                    borderRadius: 4,
                    bgcolor: 'rgba(148, 163, 184, 0.55)',
                  },
                }}
              >
                {visibleRooms.map((room) => (
                  <RoomListRow
                    key={room.id ?? room.name}
                    room={room}
                    selected={selectedRoom && (selectedRoom.id ?? selectedRoom.name) === (room.id ?? room.name)}
                    onSelect={handleRoomSelect}
                  />
                ))}
              </Box>
            )}
          </Paper>
        </Grid>

        {/* Right: detail (~70%) */}
        <Grid item xs={12} md={8}>
          {selectedRoom ? (
            <Paper
              elevation={0}
              sx={{
                p: { xs: 2, md: 3 },
                borderRadius: `${theme.radius.lg}px`,
                border: `1px solid ${theme.colors.borderDefault}`,
                bgcolor: theme.colors.bgPrimary,
                position: { md: 'sticky' },
                top: { md: 88 },
              }}
            >
              <Box sx={{ mb: 2.5 }}>
                <Typography sx={{ fontSize: 13, color: theme.colors.textSecondary, mb: 0.25 }}>
                  {typeHeadingLabel(selectedRoom.type)}
                </Typography>
                <Typography sx={{ fontSize: 28, fontWeight: 700, color: theme.colors.textPrimary, lineHeight: 1.15 }}>
                  {selectedRoom.name}
                </Typography>
              </Box>

              {detailLoading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                  <CircularProgress size={28} sx={{ color: theme.colors.brandPrimary }} />
                </Box>
              )}

              {/* Stats row */}
              <Grid container spacing={1.5} sx={{ mb: 2.5 }}>
                {[
                  { label: 'Lifetime hours', value: stats?.lifetime },
                  { label: 'Sessions', value: stats?.sessions },
                  { label: 'Avg duration', value: stats?.avgDuration },
                  { label: 'Last session', value: stats?.lastSession },
                ].map((tile) => (
                  <Grid item xs={6} sm={3} key={tile.label}>
                    <Box
                      sx={{
                        borderRadius: '10px',
                        bgcolor: 'rgba(241, 245, 249, 0.85)',
                        px: 1.5,
                        py: 1.75,
                        border: `1px solid ${theme.colors.borderDefault}`,
                      }}
                    >
                      <Typography sx={{ fontSize: 11, fontWeight: 600, color: theme.colors.textSecondary, mb: 0.75, letterSpacing: '0.04em' }}>
                        {tile.label}
                      </Typography>
                      <Typography sx={{ fontSize: 18, fontWeight: 700, color: theme.colors.textPrimary }}>{tile.value}</Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>

              <DashboardInsightCard
                title="Insights from this view"
                insightsFromView={roomDetail?.insightsFromView}
                legacyInsight={roomDetail?.insight}
                source={roomInsightSource}
                loading={detailLoading}
                disclaimerMode="short"
                attributionLive="Generated by AI from your current room view data."
                attributionCached="Generated by AI from your room view data."
                showFreshnessChip
                adminCacheInvalidation={
                  selectedRoom?.id
                    ? {
                        surface: 'room',
                        cacheParams: {
                          email: clientEmail || getEmailFromLoginToken() || '',
                          roomId: String(selectedRoom.id),
                        },
                        onInvalidated: () => handleRoomSelect(selectedRoom),
                      }
                    : undefined
                }
                sx={{ mb: 2.5 }}
              />

              {/* Recent sessions */}
              {roomDetail?.recentSessions?.length > 0 && (
                <Box>
                  <Typography sx={{ fontSize: 16, fontWeight: 700, color: theme.colors.textPrimary, mb: 1.5 }}>
                    Recent sessions
                  </Typography>
                  <Box sx={{ borderTop: `1px solid ${theme.colors.borderDefault}` }}>
                    {roomDetail.recentSessions.slice(0, 8).map((s, i) => (
                      <Box
                        key={i}
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          gap: 2,
                          py: 1.5,
                          borderBottom: `1px solid ${theme.colors.borderDefault}`,
                          flexWrap: 'wrap',
                        }}
                      >
                        <Typography sx={{ fontSize: 14, color: theme.colors.textPrimary }}>{formatSessionLine(s)}</Typography>
                        <Typography sx={{ fontSize: 14, color: theme.colors.textSecondary, fontWeight: 500 }}>
                          {formatSessionRight(s)}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
              )}
            </Paper>
          ) : (
            <Paper
              elevation={0}
              sx={{
                p: 4,
                borderRadius: `${theme.radius.lg}px`,
                border: `1px dashed ${theme.colors.borderDefault}`,
                textAlign: 'center',
              }}
            >
              <Typography sx={{ color: theme.colors.textSecondary }}>Select a room to view details.</Typography>
            </Paper>
          )}
        </Grid>
      </Grid>
    </Box>
  );
}
